import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { DayOfWeek } from "office-ui-fabric-react";
import { PanelType } from "office-ui-fabric-react/lib/components/Panel";
import { DefaultButton } from "office-ui-fabric-react/lib/components/Button";
import { ApplicationState } from "../../../store";
import * as TimeReportingPeriodsStore from "../../../store/TimeReportingPeriodsStore";
import { getFirstDayOfWeekSetting } from "../../../store/selectors/settingsSelectors";
import ExpandablePanel from "../../common/ExpandablePanel";
import LabellableComponent from "../../common/LabellableComponent";
import DatePickerInput from "../../common/inputs/DatePickerInput";
import NumberInput from "../../common/inputs/NumberInput";
import TextInput from "../../common/inputs/TextInput";
import FlagInput from "../../common/inputs/FlagInput";
import { Validator } from "../../../validation";

type OwnProps = {
    existingPeriods: TimeReportingPeriodsStore.TimeReportingPeriod[];
    isCreating: boolean;
    timesheetApprovalsEnabled: boolean;
    onDismiss: () => void;
    onSubmit: (request: TimeReportingPeriodsStore.BulkCreatePeriodsRequest) => void;
};

type StateProps = {
    firstDayOfWeek: DayOfWeek;
};

type Props = OwnProps & StateProps;

const leapYearInDays = 366;

const validators = {
    firstPeriodStartDate: Validator.new().required().date().build(),
    periodLengthDays: Validator.new().required().int32().min(1).max(leapYearInDays).build(),
    numberOfPeriods: Validator.new().required().int32().min(1).max(100).build(),
    prefix: Validator.new().maxLength(8).build(),
    nextSequenceNumber: Validator.new().required().int32().min(1).build(),
    suffix: Validator.new().maxLength(8).build()
};

const BulkCreatePeriodsPanel = (props: Props) => {
    const { existingPeriods, isCreating, timesheetApprovalsEnabled, onDismiss, onSubmit, firstDayOfWeek } = props;

    const defaultFirstPeriodStartDate = useMemo(() => {
        const lastPeriod = existingPeriods[existingPeriods.length - 1];
        return lastPeriod ? new Date(lastPeriod.attributes.EndDate).addDays(1) : new Date().getWeekStartDate(firstDayOfWeek);
    }, [existingPeriods, firstDayOfWeek]);

    const [bulkRequest, setBulkRequest] = useState<TimeReportingPeriodsStore.BulkCreatePeriodsRequest>({
        firstPeriodStartDate: defaultFirstPeriodStartDate.toDateOnlyString(),
        periodLengthDays: 7,
        numberOfPeriods: 52,
        prefix: "",
        nextSequenceNumber: 1,
        suffix: "",
        approvalRequired: timesheetApprovalsEnabled ? true : undefined
    });

    const isFormValid = useMemo(() => {
        return Validator.isValid(validators, bulkRequest);
    }, [bulkRequest]);

    const handleSubmit = () => {
        if (!isFormValid) {
            return;
        }

        onSubmit(bulkRequest);
    };

    const onRenderHeader = (): JSX.Element => {
        return (
            <div className="ms-Panel-header">
                <div className="ms-Panel-headerText">Bulk Create Time Reporting Periods</div>
                <div className="ms-Panel-secondaryText">
                    Define the period parameters and bulk create the time reporting periods.
                </div>
            </div>
        );
    };

    const onRenderFooterContent = (): JSX.Element => {
        return (
            <div className="commands justify">
                <div>
                    <DefaultButton primary text="Bulk Create" onClick={handleSubmit} disabled={!isFormValid || isCreating} />
                    <DefaultButton text="Cancel" onClick={onDismiss} />
                </div>
            </div>
        );
    };

    return (
        <ExpandablePanel
            isOpen
            type={PanelType.custom}
            customWidth="400px"
            onDismiss={onDismiss}
            onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooterContent}
            isLightDismiss>
            <div className="panel-area bulk-create-periods">
                <div className="grid-item">
                    <LabellableComponent label="Date the first period starts" required>
                        <DatePickerInput
                            value={bulkRequest.firstPeriodStartDate}
                            onChanged={firstPeriodStartDate => setBulkRequest({ ...bulkRequest, firstPeriodStartDate })}
                            validator={validators.firstPeriodStartDate}
                        />
                    </LabellableComponent>
                </div>

                <div className="grid-item">
                    <LabellableComponent label="Length of the period (days)" required>
                        <NumberInput
                            value={bulkRequest.periodLengthDays}
                            onChanged={periodLengthDays => setBulkRequest({ ...bulkRequest, periodLengthDays })}
                            validator={validators.periodLengthDays}
                        />
                    </LabellableComponent>
                </div>

                <div className="grid-item">
                    <LabellableComponent label="Number of periods to be created" required>
                        <NumberInput
                            value={bulkRequest.numberOfPeriods}
                            onChanged={numberOfPeriods => setBulkRequest({ ...bulkRequest, numberOfPeriods })}
                            validator={validators.numberOfPeriods}
                        />
                    </LabellableComponent>
                </div>

                <div className="grid-item">
                    <LabellableComponent label="Prefix">
                        <TextInput
                            value={bulkRequest.prefix}
                            onChanged={prefix => setBulkRequest({ ...bulkRequest, prefix })}
                            validator={validators.prefix}
                            maxLength={8}
                        />
                    </LabellableComponent>
                </div>

                <div className="grid-item">
                    <LabellableComponent label="Next Sequence Number" required>
                        <NumberInput
                            value={bulkRequest.nextSequenceNumber}
                            onChanged={nextSequenceNumber => setBulkRequest({ ...bulkRequest, nextSequenceNumber })}
                            validator={validators.nextSequenceNumber}
                        />
                    </LabellableComponent>
                </div>

                <div className="grid-item">
                    <LabellableComponent label="Suffix">
                        <TextInput
                            value={bulkRequest.suffix}
                            onChanged={suffix => setBulkRequest({ ...bulkRequest, suffix })}
                            validator={validators.suffix}
                            maxLength={8}
                        />
                    </LabellableComponent>
                </div>

                {timesheetApprovalsEnabled && (
                    <div className="grid-item">
                        <LabellableComponent label="Approval Required">
                            <FlagInput
                                value={bulkRequest.approvalRequired ?? false}
                                onChanged={approvalRequired => setBulkRequest({ ...bulkRequest, approvalRequired })}
                            />
                        </LabellableComponent>
                    </div>
                )}
            </div>
        </ExpandablePanel>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    return {
        firstDayOfWeek: getFirstDayOfWeekSetting(state)
    };
};

export default connect(mapStateToProps)(BulkCreatePeriodsPanel);
